// components/common/DimensionHeader.js
//
// Unified section header for the 7 data-quality dimensions.
// Provides:
//   • Editorial breadcrumb: "data quality · {slug}"
//   • Large title + parallel-structured descriptor
//   • Optional right-aligned stat widget (current score + peak/low)
//   • Editorial underline-style sub-tab strip with optional count pills
//
// Replaces the bare <SubTabs /> call at the top of each section.
//
// Usage:
//   <DimensionHeader
//     slug="uniqueness"
//     title="Uniqueness"
//     description="Detects duplicate records and repeated values..."
//     stat={{ score: 86.2, scoreLabel: 'uniqueness score', peak: 92.4, low: 86.2,
//             trend: { direction: 'up', delta: '+6.2 pts', label: 'improving' } }}
//     tabs={[
//       { id: 'summary', label: 'Summary', icon: BarChart3 },
//       { id: 'variables', label: 'Variable Analysis', icon: Table, count: 16, countTone: 'neutral' },
//       { id: 'issues', label: 'Issue Log', icon: ClipboardList, count: 142, countTone: 'alert' },
//     ]}
//     activeTab={activeTab}
//     onTabChange={setActiveTab}
//     isMobile={isMobile}
//   />

import React, { useState, useRef, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { useTheme } from '../../utils/ThemeSwitcher';
import './DimensionHeader.css';

/* ------------------------------------------------------------------ */
/*  Mini sparkline — pure SVG, no library dependency                   */
/* ------------------------------------------------------------------ */
const Sparkline = ({ values, color, width = 96, height = 28 }) => {
  if (!values || values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = width / (values.length - 1);
  const pts = values
    .map((v, i) => `${i * step},${height - ((v - min) / range) * height}`)
    .join(' ');
  const last = values[values.length - 1];
  const lastX = (values.length - 1) * step;
  const lastY = height - ((last - min) / range) * height;
  return (
    <svg width={width} height={height} style={{ overflow: 'visible' }} aria-hidden="true">
      <defs>
        <linearGradient id="dh-spark-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon
        points={`0,${height} ${pts} ${width},${height}`}
        fill="url(#dh-spark-fill)"
      />
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={lastX} cy={lastY} r="2.5" fill={color} />
    </svg>
  );
};

/* ------------------------------------------------------------------ */
/*  Trend chip — small pill with arrow + delta                         */
/* ------------------------------------------------------------------ */
const TrendChip = ({ trend, isDarkMode }) => {
  if (!trend) return null;
  const dir = trend.direction || 'flat';
  const Icon = dir === 'up' ? TrendingUp : dir === 'down' ? TrendingDown : Minus;
  const palette = {
    up:   { light: { bg: 'rgba(16,185,129,0.10)', fg: '#047857' },
            dark:  { bg: 'rgba(16,185,129,0.14)', fg: '#34D399' } },
    down: { light: { bg: 'rgba(239,68,68,0.10)',  fg: '#B91C1C' },
            dark:  { bg: 'rgba(239,68,68,0.16)',  fg: '#F87171' } },
    flat: { light: { bg: 'rgba(107,114,128,0.10)', fg: '#4B5563' },
            dark:  { bg: 'rgba(156,163,175,0.14)', fg: '#9CA3AF' } },
  };
  const c = palette[dir][isDarkMode ? 'dark' : 'light'];

  return (
    <span
      className="dh-trend-chip"
      style={{ background: c.bg, color: c.fg }}
    >
      <Icon size={11} strokeWidth={2.4} />
      {trend.delta || trend.label}
    </span>
  );
};

/* ------------------------------------------------------------------ */
/*  Count pill — used on sub-tabs                                      */
/* ------------------------------------------------------------------ */
const CountPill = ({ value, tone = 'neutral', isActive, isDarkMode }) => {
  if (value == null) return null;
  const tones = {
    neutral: {
      light: { bg: 'rgba(15,23,42,0.06)',  fg: '#475569' },
      dark:  { bg: 'rgba(255,255,255,0.07)', fg: '#94A3B8' },
      active: { light: { bg: '#0F172A', fg: '#FFFFFF' },
                dark:  { bg: 'rgba(255,255,255,0.12)', fg: '#F1F5F9' } },
    },
    alert: {
      light: { bg: 'rgba(220,38,38,0.09)', fg: '#B91C1C' },
      dark:  { bg: 'rgba(248,113,113,0.14)', fg: '#FCA5A5' },
      active: { light: { bg: '#B91C1C', fg: '#FFFFFF' },
                dark:  { bg: 'rgba(248,113,113,0.22)', fg: '#FECACA' } },
    },
    success: {
      light: { bg: 'rgba(16,185,129,0.10)', fg: '#047857' },
      dark:  { bg: 'rgba(52,211,153,0.14)', fg: '#6EE7B7' },
      active: { light: { bg: '#047857', fg: '#FFFFFF' },
                dark:  { bg: 'rgba(52,211,153,0.22)', fg: '#A7F3D0' } },
    },
  };
  const conf = tones[tone] || tones.neutral;
  const variant = isActive ? conf.active[isDarkMode ? 'dark' : 'light']
                           : conf[isDarkMode ? 'dark' : 'light'];

  return (
    <span
      className="dh-count-pill"
      style={{
        background: variant.bg,
        color: variant.fg,
      }}
    >
      {typeof value === 'number' ? value.toLocaleString() : value}
    </span>
  );
};

/* ================================================================== */
/*  MAIN COMPONENT                                                     */
/* ================================================================== */
const DimensionHeader = ({
  slug,
  title,
  description,
  eyebrow = 'data quality',
  stat,           // { score, scoreLabel, peak, low, sparkline, trend }
  tabs = [],
  activeTab,
  onTabChange,
  isMobile = false,
  isSidePanelExpanded = true,
  defaultCollapsed = false,
  rememberCollapseKey,    // e.g. "uniqueness" — persists per-user
  rightSlot = null,       // escape hatch for custom right-side content
}) => {
  const { isDarkMode } = useTheme();

  // Persisted collapse state for the descriptor (power users can hide it)
  const storageKey = rememberCollapseKey
    ? `dh:collapsed:${rememberCollapseKey}`
    : null;

  const [collapsed, setCollapsed] = useState(() => {
    if (storageKey) {
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved != null) return saved === '1';
      } catch (_) { /* ignore */ }
    }
    return defaultCollapsed;
  });

  useEffect(() => {
    if (!storageKey) return;
    try { localStorage.setItem(storageKey, collapsed ? '1' : '0'); } catch (_) {}
  }, [collapsed, storageKey]);

  const palette = {
    eyebrowDot:  isDarkMode ? '#60A5FA' : '#2563EB',
    eyebrowText: isDarkMode ? '#6B7280' : '#94A3B8',
    title:       isDarkMode ? '#F1F5F9' : '#0F172A',
    body:        isDarkMode ? '#94A3B8' : '#475569',
    divider:     isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.08)',
    tabRest:     isDarkMode ? '#9CA3AF' : '#64748B',
    tabActive:   isDarkMode ? '#F1F5F9' : '#0F172A',
    tabIndicator:isDarkMode ? '#F59E0B' : '#D97706',
    statLabel:   isDarkMode ? '#6B7280' : '#94A3B8',
    statValue:   isDarkMode ? '#F1F5F9' : '#0F172A',
    peakColor:   isDarkMode ? '#34D399' : '#047857',
    lowColor:    isDarkMode ? '#F87171' : '#B91C1C',
  };

  /* ---------- Tab strip refs for animated underline ---------------- */
  const tabRefs = useRef({});
  const [indicator, setIndicator] = useState({ left: 0, width: 0, ready: false });

  useEffect(() => {
    const node = tabRefs.current[activeTab];
    if (node) {
      const rect = node.getBoundingClientRect();
      const parent = node.parentElement.getBoundingClientRect();
      setIndicator({
        left: rect.left - parent.left,
        width: rect.width,
        ready: true,
      });
    }
  }, [activeTab, tabs, isMobile]);

  /* ================================================================ */
  /*  RENDER                                                          */
  /* ================================================================ */
  return (
    <div
      className="dimension-header"
      style={{
        fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
        paddingTop: isMobile ? '16px' : '24px',
        marginBottom: isMobile ? '20px' : '28px',
      }}
    >
      {/* ---------- Top row: hero text + optional stat widget ------- */}
      <div
        className="dh-hero"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: isMobile ? '16px' : '48px',
          flexWrap: isMobile ? 'wrap' : 'nowrap',
        }}
      >
        {/* ------- Left: breadcrumb, title, description ------------ */}
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
          {/* Breadcrumb eyebrow */}
          <div
            className="dh-eyebrow"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '11px',
              letterSpacing: '0.04em',
              color: palette.eyebrowText,
              fontWeight: 500,
              textTransform: 'lowercase',
              marginBottom: '14px',
            }}
          >
            <span
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: palette.eyebrowDot,
                display: 'inline-block',
                boxShadow: `0 0 0 3px ${isDarkMode ? 'rgba(96,165,250,0.12)' : 'rgba(37,99,235,0.12)'}`,
              }}
            />
            {eyebrow}
            <span style={{ opacity: 0.4 }}>·</span>
            <span style={{ color: palette.title, fontWeight: 600 }}>{slug}</span>
          </div>

          {/* Title */}
          <h1
            className="dh-title"
            style={{
              fontSize: isMobile ? '28px' : '34px',
              lineHeight: 1.05,
              fontWeight: 700,
              letterSpacing: '-0.025em',
              color: palette.title,
              margin: 0,
              marginBottom: collapsed ? 0 : '12px',
            }}
          >
            {title}
          </h1>

          {/* Description (collapsible) */}
          {!collapsed && description && (
            <p
              className="dh-description"
              style={{
                fontSize: isMobile ? '13px' : '14px',
                lineHeight: 1.55,
                color: palette.body,
                margin: 0,
                maxWidth: '620px',
                letterSpacing: '-0.005em',
              }}
            >
              {description}
            </p>
          )}

          {/* Collapse toggle (only when rememberCollapseKey provided) */}
          {rememberCollapseKey && description && (
            <button
              onClick={() => setCollapsed((c) => !c)}
              className="dh-collapse-toggle"
              style={{
                marginTop: collapsed ? '6px' : '10px',
                background: 'transparent',
                border: 'none',
                color: palette.eyebrowText,
                fontSize: '11px',
                letterSpacing: '0.02em',
                cursor: 'pointer',
                padding: '4px 0',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                fontFamily: 'inherit',
              }}
              aria-label={collapsed ? 'Show description' : 'Hide description'}
            >
              {collapsed ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
              {collapsed ? 'show description' : 'hide'}
            </button>
          )}
        </div>

        {/* ------- Right: optional stat widget --------------------- */}
        {(stat || rightSlot) && !isMobile && (
          <div
            className="dh-stat"
            style={{
              flex: '0 0 auto',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '24px',
              paddingTop: '4px',
            }}
          >
            {rightSlot}
            {stat && (
              <>
                {/* Sparkline */}
                {stat.sparkline && stat.sparkline.length > 1 && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                    <Sparkline
                      values={stat.sparkline}
                      color={
                        stat.trend?.direction === 'down'
                          ? (isDarkMode ? '#F87171' : '#DC2626')
                          : (isDarkMode ? '#34D399' : '#10B981')
                      }
                    />
                    {stat.trend && <TrendChip trend={stat.trend} isDarkMode={isDarkMode} />}
                  </div>
                )}

                {/* Peak / Low pair */}
                {(stat.peak != null || stat.low != null) && (
                  <div style={{ display: 'flex', gap: '20px' }}>
                    {stat.peak != null && (
                      <div style={{ textAlign: 'right' }}>
                        <div style={{
                          fontSize: '10px',
                          color: palette.statLabel,
                          textTransform: 'lowercase',
                          letterSpacing: '0.04em',
                          marginBottom: '2px',
                        }}>
                          peak
                        </div>
                        <div style={{
                          fontSize: '15px',
                          fontWeight: 600,
                          color: palette.peakColor,
                          fontVariantNumeric: 'tabular-nums',
                          letterSpacing: '-0.01em',
                        }}>
                          {formatStatValue(stat.peak, stat.unit)}
                        </div>
                      </div>
                    )}
                    {stat.low != null && (
                      <div style={{ textAlign: 'right' }}>
                        <div style={{
                          fontSize: '10px',
                          color: palette.statLabel,
                          textTransform: 'lowercase',
                          letterSpacing: '0.04em',
                          marginBottom: '2px',
                        }}>
                          low
                        </div>
                        <div style={{
                          fontSize: '15px',
                          fontWeight: 600,
                          color: palette.lowColor,
                          fontVariantNumeric: 'tabular-nums',
                          letterSpacing: '-0.01em',
                        }}>
                          {formatStatValue(stat.low, stat.unit)}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Primary current score, if no peak/low provided */}
                {stat.score != null && stat.peak == null && stat.low == null && (
                  <div style={{ textAlign: 'right' }}>
                    {stat.scoreLabel && (
                      <div style={{
                        fontSize: '10px',
                        color: palette.statLabel,
                        textTransform: 'lowercase',
                        letterSpacing: '0.04em',
                        marginBottom: '2px',
                      }}>
                        {stat.scoreLabel}
                      </div>
                    )}
                    <div style={{
                      fontSize: '22px',
                      fontWeight: 700,
                      color: palette.statValue,
                      fontVariantNumeric: 'tabular-nums',
                      letterSpacing: '-0.02em',
                      lineHeight: 1,
                    }}>
                      {formatStatValue(stat.score, stat.unit)}
                    </div>
                    {stat.trend && (
                      <div style={{ marginTop: '6px', display: 'flex', justifyContent: 'flex-end' }}>
                        <TrendChip trend={stat.trend} isDarkMode={isDarkMode} />
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* ---------- Divider ---------------------------------------- */}
      {tabs && tabs.length > 0 && (
        <>
          <div
            style={{
              height: '1px',
              background: palette.divider,
              marginTop: isMobile ? '20px' : '28px',
              marginBottom: 0,
            }}
          />

          {/* ---------- Sub-tab strip (editorial underline) -------- */}
          <div
            className="dh-tabs"
            role="tablist"
            style={{
              display: 'flex',
              alignItems: 'stretch',
              gap: isMobile ? '4px' : '4px',
              position: 'relative',
              overflowX: 'auto',
              overflowY: 'hidden',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              paddingTop: '2px',
            }}
          >
            {tabs.map((tab) => {
              const isActive = tab.id === activeTab;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  ref={(el) => { if (el) tabRefs.current[tab.id] = el; }}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => onTabChange && onTabChange(tab.id)}
                  className={`dh-tab ${isActive ? 'dh-tab-active' : ''}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: isMobile ? '14px 10px' : '16px 14px',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: 0,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: isMobile ? '13px' : '13.5px',
                    fontWeight: isActive ? 600 : 500,
                    letterSpacing: '-0.005em',
                    color: isActive ? palette.tabActive : palette.tabRest,
                    whiteSpace: 'nowrap',
                    transition: 'color 0.18s ease',
                    position: 'relative',
                  }}
                >
                  {Icon && <Icon size={14} strokeWidth={isActive ? 2.25 : 1.75} />}
                  <span>{tab.label}</span>
                  {tab.count != null && (
                    <CountPill
                      value={tab.count}
                      tone={tab.countTone || 'neutral'}
                      isActive={isActive}
                      isDarkMode={isDarkMode}
                    />
                  )}
                </button>
              );
            })}

            {/* Animated underline indicator */}
            <div
              className="dh-tab-indicator"
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                height: '2px',
                background: palette.tabIndicator,
                borderRadius: '2px 2px 0 0',
                transform: `translateX(${indicator.left}px)`,
                width: `${indicator.width}px`,
                transition: indicator.ready
                  ? 'transform 0.32s cubic-bezier(0.4, 0, 0.2, 1), width 0.32s cubic-bezier(0.4, 0, 0.2, 1)'
                  : 'none',
                opacity: indicator.ready ? 1 : 0,
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  helpers                                                            */
/* ------------------------------------------------------------------ */
function formatStatValue(value, unit) {
  if (value == null) return '—';
  if (typeof value !== 'number') return value;
  const formatted = Number.isInteger(value) ? value.toLocaleString() : value.toFixed(1);
  if (unit === '%') return `${formatted}%`;
  if (unit) return `${formatted}${unit}`;
  return formatted;
}

export default DimensionHeader;
